import React, {Fragment, useEffect, useState} from "react";
import {ConfigProvider, Form, Input, InputNumber} from "antd";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";

import ApiService from "../../../services/ApiService";
import {getLayoutDirection} from "../../../locales";
import PhoneInput from "../../../components/WebEditor/components/PhoneInput";
import {goalProgress, initials} from "./model";
import {GoalProgress} from "./Progress";
import {isPreviewRequest} from "./preview";
import styles from "./v2.module.css";

/**
 * « Devenir ambassadeur » : porter la campagne auprès de ses proches, avec sa propre
 * page et sa propre jauge. L'inscription tient en quatre champs ; le lien de l'espace
 * personnel part par email, sans mot de passe à retenir.
 */
export function AmbassadeurSignup({context, campagneData, palette, preview = false}) {
    const readOnly = preview || isPreviewRequest(context);
    const [open, setOpen] = useState(false);
    const [done, setDone] = useState(null);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);

    // Créer son équipe n'est pas restreint en nouveau design : le bloc s'affiche sauf si
    // le responsable a fermé les inscriptions (même règle que l'API, signupOpen).
    const ambsSignup = campagneData?.config?.ambsSignup;
    if (ambsSignup === false || (ambsSignup !== true && campagneData?.config?.template !== "V2")) return null;

    const submit = async (values) => {
        if (readOnly || sending) return;
        setSending(true);
        setError(null);

        let res = null;
        try {
            res = await ApiService.post("/campagne/ambassadeur/" + campagneData.id, {
                prenom: values.prenom,
                nom: values.nom,
                email: values.email,
                tel: values.tel || null,
                label: values.label || null,
                objectif: values.objectif ?? null,
            });
        } catch (e) {
            // Un refus arrive en erreur HTTP : le formulaire doit redevenir utilisable.
            res = e?.response?.data || null;
        }
        setSending(false);

        if (res?.success) {
            setDone(res);
            return;
        }
        if (res?.error === "SIGNUP_CLOSED") {
            setError(t`Les inscriptions sont fermées pour cette campagne.`);
            return;
        }
        if (res?.error === "ALREADY_SIGNED_UP") {
            // Déjà ambassadeur : inutile d'en créer une seconde, le lien de son espace
            // vient de lui être renvoyé par email.
            setError(t`Vous avez déjà une page pour cette campagne. Le lien de votre espace vient de vous être renvoyé par email.`);
            return;
        }
        setError(t`Votre inscription n'a pas pu être enregistrée. Vérifiez vos coordonnées.`);
    };

    if (done) {
        return (
            <section className={`${styles.block} ${styles.blockSoft}`} aria-labelledby="cv-amb-title">
                <div className={styles.blockInner}>
                    <h2 id="cv-amb-title" className={styles.blockTitle}><Trans>Votre page d'ambassadeur</Trans></h2>
                    <p className={styles.help}>
                        {done.pending
                            ? <Trans>Merci ! Votre page sera visible dès que le responsable l'aura validée. Le lien de votre espace vient de vous être envoyé par email.</Trans>
                            : <Trans>Votre page est en ligne. Le lien de votre espace vient de vous être envoyé par email.</Trans>}
                    </p>
                    <a className={styles.secondary} href={done.url}><Trans>Ouvrir mon espace</Trans></a>
                </div>
            </section>
        );
    }

    return (
        <section className={`${styles.block} ${styles.blockSoft}`} aria-labelledby="cv-amb-title">
            <div className={styles.blockInner}>
                <h2 id="cv-amb-title" className={styles.blockTitle}><Trans>Devenir ambassadeur</Trans></h2>
                <p className={styles.help}>
                    <Trans>Collectez pour cette campagne auprès de vos proches : vous aurez votre page, votre jauge et votre lien à partager.</Trans>
                </p>

                {!open && (
                    <button type="button" className={styles.secondary} onClick={() => setOpen(true)}>
                        <Trans>Je deviens ambassadeur</Trans>
                    </button>
                )}

                {open && (
                    <ConfigProvider
                        direction={getLayoutDirection(context.langValue)}
                        theme={{token: {colorPrimary: palette.accent, borderRadius: 12, fontFamily: "inherit", controlHeight: 48, fontSize: 16}}}
                    >
                        {error && <p className={styles.error} role="alert">{error}</p>}
                        <Form layout="vertical" requiredMark={false} onFinish={submit} style={{marginTop: 12}}>
                            <Form.Item label={t`Prénom`} name="prenom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                <Input autoComplete="given-name"/>
                            </Form.Item>
                            <Form.Item label={t`Nom`} name="nom" rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                <Input autoComplete="family-name"/>
                            </Form.Item>
                            <Form.Item
                                label={t`Email`}
                                name="email"
                                rules={[
                                    {required: true, message: t`Ce champ est obligatoire`},
                                    {type: "email", message: t`Cette adresse email n'est pas valide`},
                                ]}
                                extra={t`Le lien de votre espace y sera envoyé.`}
                            >
                                <Input type="email" autoComplete="email"/>
                            </Form.Item>
                            <Form.Item label={t`Téléphone (facultatif)`} name="tel">
                                <PhoneInput placeholder={t`Téléphone`} defaultLang={context.langValue}/>
                            </Form.Item>
                            <Form.Item label={t`Nom de votre équipe (facultatif)`} name="label" extra={t`Par défaut, votre prénom et votre nom.`}>
                                <Input maxLength={200}/>
                            </Form.Item>
                            <Form.Item label={t`Votre objectif (facultatif)`} name="objectif">
                                <InputNumber min={0} style={{width: 200}}/>
                            </Form.Item>

                            <button type="submit" className={styles.primary} disabled={readOnly || sending}>
                                {sending ? t`Un instant…` : t`Créer ma page`}
                            </button>
                        </Form>
                    </ConfigProvider>
                )}
            </div>
        </section>
    );
}

/**
 * Espace personnel de l'ambassadeur, ouvert par son lien : où en est son équipe, son
 * lien à partager, et le message prêt à envoyer sur WhatsApp.
 */
export function AmbassadeurSpace({context, token, formatMoney, formatNumber}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [copied, setCopied] = useState(false);
    // Réglages de l'ambassadeur : son espace ne faisait que LIRE, alors que l'API sait
    // déjà enregistrer son nom d'équipe, son objectif, son mot d'accueil et ses alertes.
    const [reglages, setReglages] = useState(null);
    const [enregistre, setEnregistre] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const [refus, setRefus] = useState(null);

    useEffect(() => {
        let cancelled = false;
        ApiService.get("/campagne/ambassadeur/space/" + token).then(res => {
            if (cancelled) return;
            if (res?.success) {
                setData(res.data);
                setReglages({
                    label: res.data.team?.label || "",
                    objectif: res.data.team?.objectif ?? "",
                    description: res.data.team?.description || "",
                    notifyDonations: res.data.team?.notifyDonations !== false,
                });
            }
            else setError(true);
        }).catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [token]);

    if (error) {
        return (
            <section className={styles.ambSpace} role="status">
                <Trans>Ce lien n'est plus valable. Demandez-en un nouveau au responsable de la campagne.</Trans>
            </section>
        );
    }
    if (!data) return null;

    const title = data.campagne?.config?.title || data.campagne?.label;
    const message = t`Je collecte pour « ${title} ». Vous pouvez m'aider ici :`;
    const share = "https://wa.me/?text=" + encodeURIComponent(message + " " + data.teamUrl);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data.teamUrl);
            setCopied(true);
        } catch (e) {
            setCopied(false);
        }
    };

    const enregistrer = async (event) => {
        event.preventDefault();
        setEnCours(true);
        setRefus(null);
        try {
            const res = await ApiService.post("/campagne/ambassadeur/space/" + token, {
                label: reglages.label,
                objectif: reglages.objectif === "" ? 0 : Number(String(reglages.objectif).replace(",", ".")),
                description: reglages.description,
                notifyDonations: reglages.notifyDonations,
            });
            if (res?.success) {
                setData(current => ({...current, team: res.data, goal: {...current.goal, target: Number(res.data?.objectif) || 0}}));
                setEnregistre(true);
            } else {
                setRefus(t`Vos modifications n'ont pas pu être enregistrées.`);
            }
        } catch (e) {
            setRefus(t`Vos modifications n'ont pas pu être enregistrées.`);
        }
        setEnCours(false);
    };

    const champ = (cle) => (event) => {
        const valeur = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setEnregistre(false);
        setReglages(current => ({...current, [cle]: valeur}));
    };

    return (
        <section className={styles.ambSpace} aria-labelledby="cv-amb-space-title">
            <div className={styles.ambSpaceHead}>
                <span className={styles.avatar} aria-hidden="true">{initials(data.team?.label)}</span>
                <div>
                    <h2 id="cv-amb-space-title" className={styles.ambSpaceTitle}>
                        <Trans>Votre espace, {data.team?.label}</Trans>
                    </h2>
                    {data.team?.pending && (
                        <p className={styles.help}><Trans>Votre page attend la validation du responsable.</Trans></p>
                    )}
                </div>
            </div>

            <GoalProgress
                goal={data.goal}
                progress={data.progress}
                matched={null}
                formatMoney={formatMoney}
                formatNumber={formatNumber}
            />

            <div className={styles.shareRow}>
                <a className={`${styles.shareButton} ${styles.shareWhatsapp}`} href={share} target="_blank" rel="noopener noreferrer">
                    <Trans>Partager sur WhatsApp</Trans>
                </a>
                <button type="button" className={styles.shareButton} onClick={copy}>
                    {copied ? <Trans>Lien copié</Trans> : <Trans>Copier mon lien</Trans>}
                </button>
            </div>

            {reglages && (
                <form className={styles.ambForm} onSubmit={enregistrer}>
                    <h3 className={styles.ambFormTitle}><Trans>Ma page</Trans></h3>

                    <label className={styles.fieldLabel} htmlFor="cv-amb-label"><Trans>Nom de mon équipe</Trans></label>
                    <input id="cv-amb-label" className={styles.input} value={reglages.label} onChange={champ("label")} maxLength={200}/>

                    <label className={styles.fieldLabel} htmlFor="cv-amb-objectif"><Trans>Mon objectif</Trans></label>
                    <input id="cv-amb-objectif" className={styles.input} inputMode="decimal" value={reglages.objectif} onChange={champ("objectif")}/>

                    <label className={styles.fieldLabel} htmlFor="cv-amb-description"><Trans>Mon message</Trans></label>
                    <textarea id="cv-amb-description" className={styles.input} rows={3} maxLength={500} value={reglages.description} onChange={champ("description")}/>

                    <label className={styles.ambCheckbox} htmlFor="cv-amb-notify">
                        <input id="cv-amb-notify" type="checkbox" checked={reglages.notifyDonations} onChange={champ("notifyDonations")}/>
                        <span><Trans>Me prévenir par email à chaque don</Trans></span>
                    </label>

                    {refus && <p className={styles.help} role="alert">{refus}</p>}

                    <button type="submit" className={styles.secondary} disabled={enCours}>
                        {enregistre ? <Trans>Enregistré</Trans> : <Trans>Enregistrer</Trans>}
                    </button>
                </form>
            )}
        </section>
    );
}
